import React, { useState } from 'react';
import { processHaltReason, mlsProcessSuspended, orgOverCapacity } from './tenancy';
import { api } from './api';
import Icon from './Icon';
import './Process.css';

// Process control for iBetterAgent operators (System + MLS). Reminders are sent by
// the SERVER (a daily scheduled pass); this tab is where an operator pauses/resumes
// that processing and can trigger a run on the server on demand. There is no longer
// a browser-based send loop — the server is the single engine, so dev and prod behave
// the same.
function Process({ accounts, orgs = [], mlsList = [], settings = {}, level = 'system', scopeName = '', mlsId = null, onToggleSystemSuspend, onSetMlsProcess }) {
  // Informational eligibility count (the server recomputes authoritatively at run time).
  const eligible = accounts.filter(a => !a.system && !a.disabled);
  const orgOf = (a) => a.orgId ? orgs.find(o => o.id === a.orgId) : null;
  const isHalted = (a) => !!processHaltReason(a, orgs, mlsList, settings) || orgOverCapacity(accounts, orgOf(a));
  const users = eligible.filter(a => !isHalted(a));
  const suspendedCount = eligible.length - users.length;

  // The MLS being managed + effective stopped state. MLS is suspend-only (2-state),
  // like an org: `scopeStopped` is true if the MLS paused itself OR System is paused.
  const mls = mlsId ? mlsList.find(m => m.id === mlsId) : null;
  const mlsSelfPaused = !!(mls && mls.processSuspended);
  const scopeStopped = level === 'mls' ? mlsProcessSuspended(mls, settings) : !!settings.processSuspended;

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // Trigger a server-side pass now (System level only — it runs the whole system-wide
  // pass synchronously and returns the summary). Idempotency on the server means a
  // repeat click never double-sends. MLS participation is governed by the switch below.
  async function runNow() {
    setRunning(true); setErr(''); setResult(null);
    try {
      const r = await api.post('/api/cron/run-now', {});
      setResult(r);
    } catch (e) {
      setErr((e && e.message) || 'Run failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="proc-container">
      <div className="proc-header">
        <div>
          <h2>Process{level === 'mls' && scopeName ? ` — ${scopeName}` : ''}</h2>
          <p>{level === 'mls'
            ? `Reminders for ${scopeName} are sent automatically by the server on a daily schedule. Use the control below to set whether this MLS participates.`
            : 'Reminders are sent automatically by the server on a daily schedule. Pause or resume all processing below, or run a pass on the server right now.'}</p>
        </div>
        {/* On-demand run is System-only — it runs the whole system-wide pass. An MLS
            just chooses whether to participate via its switch below. */}
        {level !== 'mls' && (
          <div className="proc-header-actions">
            <button className="proc-run-btn" onClick={runNow}
              disabled={running || scopeStopped || users.length === 0}
              title={scopeStopped ? 'Resume processing first' : 'Run a processing pass on the server now'}>
              <Icon name="launch" size={16} /> {running ? 'Running…' : 'Run on server now'}
            </button>
          </div>
        )}
      </div>

      {/* Process pause toggle — the SAME single button as the org/agent "Pause
          Reminders" control (turns red when paused). System pauses everything; an MLS
          pauses only its own scope (it can opt out but cannot override a System pause). */}
      <div className="proc-control">
        {(() => {
          const paused = level === 'mls' ? mlsSelfPaused : !!settings.processSuspended;
          const setPaused = (v) => level === 'mls'
            ? (onSetMlsProcess && onSetMlsProcess(v))
            : (onToggleSystemSuspend && onToggleSystemSuspend(v));
          const label = level === 'mls' ? 'Pause This MLS' : 'Pause All Processing';
          return (
            <button className={`rt-new-btn rt-pause-btn ${paused ? 'paused' : ''}`}
              onClick={() => setPaused(!paused)}
              title={paused ? 'Resume processing' : label}>
              <Icon name="pause" size={16} /> {paused ? 'Paused' : label}
            </button>
          );
        })()}
        <p className="proc-control-hint">{level === 'mls'
          ? 'When paused, no reminders are sent for any agent in this MLS. Reminders are also paused automatically whenever the whole system is paused.'
          : 'Pauses reminder processing for every MLS — and acts as an emergency stop: it also halts a send that’s already in progress within a couple of seconds. An MLS can additionally pause just itself on its own Process tab.'}</p>
      </div>

      {scopeStopped && (
        <div className="proc-suspended-banner">
          {level === 'mls' && !mlsSelfPaused
            ? <>■ Processing is <strong>paused system-wide</strong> — resume it on the System → Process tab.</>
            : <>■ Processing is <strong>paused</strong> for {level === 'mls' ? scopeName : 'the whole system'} — no reminders will be sent{level === 'mls' ? ' for this MLS' : ''} until it’s resumed above.</>}
        </div>
      )}

      <div className="proc-summary">
        <span><strong>{users.length}</strong> agent{users.length !== 1 ? 's' : ''} eligible{level === 'mls' ? ' in this MLS' : ''}</span>
        {suspendedCount > 0 && <span className="proc-suspended"><Icon name="pause" size={16} /> {suspendedCount} suspended</span>}
      </div>

      {err && <div className="proc-health warn">⚠ {err}</div>}

      {result && (
        <div className="proc-status">
          <div className="proc-status-line">
            {result.skipped
              ? <>■ Pass skipped — {result.reason === 'already-running' ? 'another run is already in progress' : result.reason === 'email-not-configured' ? 'email is not configured on the server' : result.reason}.</>
              : <>✅ Server run complete — processed <strong>{result.agentsProcessed}</strong> agent{result.agentsProcessed !== 1 ? 's' : ''}, sent <strong>{result.sent}</strong>{result.failed ? `, ${result.failed} failed` : ''}{result.alreadySent ? `, ${result.alreadySent} already sent` : ''}.</>}
          </div>
        </div>
      )}

      {users.length === 0 && (
        <div className="proc-empty"><span><Icon name="contacts" size={20} /></span><p>No eligible agents to process{level === 'mls' ? ' in this MLS' : ''}.</p></div>
      )}
    </div>
  );
}

export default Process;
